/**
 * VISITOR PASS SCHEDULES — the sentence and the preview (resident-portal RP-2.9).
 *
 * Two jobs, both for the pass editor and the lists around it:
 *
 *   describeSchedule(spec)                    "Every Tuesday, 09:00–13:00, until 31 Dec 2026"
 *   previewOccurrences(spec, zone, from, n)   the next n visits, as instants
 *
 * ═══════════ THE PREVIEW IS NOT THE TRUTH ═══════════
 * The ONE engine is lb_pass_occurrences (migration 115); every server answer —
 * the desk's expected list, a pass's `next`, a skip check — comes from it. This
 * file mirrors it so the editor can say "next visits: …" while somebody types,
 * before anything is saved. When the two disagree, the server is right and this
 * is a bug here. The mirror is held to the engine by running EVERY case of the
 * backend's case file (utils/__fixtures__/occurrence-cases.json, a checked copy —
 * lobbero_be/scripts/passes/check-fe-copy.js fails when it drifts).
 *
 * THE RULES IT MIRRORS
 *   * All-day = the desk day of its local date: [D 07:00, D+1 07:00) in the
 *     building zone (DESK_DAY_START_HOUR, lobbero_be/src/desk-day.js).
 *   * Timed = the wall-clock window in the building zone. An end at or before
 *     the start crosses midnight and belongs to its START date.
 *   * "Every n weeks" is anchored on the ISO week (Monday) of valid_from, not on
 *     the week number — so it holds across a year boundary.
 *   * Revoked -> nothing. Paused -> nothing up to and including paused_until
 *     (indefinitely when there is none). Skip dates drop their day.
 *   * A wall time the zone skips (spring forward) moves forward by the gap; a
 *     wall time the zone repeats (fall back) is the LATER instant, standard
 *     time. That is what Postgres does for `timestamp AT TIME ZONE`, and the
 *     DST cases in the fixture pin it.
 *
 * EVERY Intl CALL IS GUARDED. `properties.timezone` is free text as far as this
 * client knows; a zone Intl rejects falls back to UTC — exactly as the server's
 * lb_safe_timezone does — rather than throwing inside a render.
 *
 * Dates here are calendar dates ('YYYY-MM-DD') and are handled as whole UTC
 * days, never through `new Date('YYYY-MM-DD')` in the device zone, which lands
 * on the previous day for anyone west of Greenwich.
 */
import { formatDate } from '../function-utils/locale';
import { countOf } from '../function-utils/plural';
import {
  DESK_DAY_START_HOUR as TIME_DESK_DAY_START_HOUR,
  calendarDayOf as timeCalendarDayOf,
  clockTime,
  deskDayOf as timeDeskDayOf,
  deviceZone as timeDeviceZone,
  offsetAt as timeOffsetAt,
  safeZone as timeSafeZone,
  sameClock,
  zoneLabel,
} from '../function-utils/building-time';
import type {
  PassOccurrence,
  PassScheduleKind,
  PassStatus,
} from '../interfaces/visitor-pass';

/** The desk day starts at 07:00 in the building zone (BE src/desk-day.js). */
export const DESK_DAY_START_HOUR = TIME_DESK_DAY_START_HOUR;

/**
 * The building's DESK date ('YYYY-MM-DD'): its calendar date, or the day
 * before when its wall clock is before 07:00 (lobbero_be/src/desk-day.js
 * deskDayKey — decided on the wall-clock hour). It is the earliest day a NEW
 * visitor pass may start (migration 129): at 00:30 "for tonight" is still
 * yesterday's desk day, and the desk expects that visitor until 07:00. A zone
 * Intl rejects falls back to the reader's own clock, as the portal's other
 * dates do. Implemented once, in function-utils/building-time.
 *
 * A function of this module's own (not a re-export) because pass-editor's
 * tests spy on it here.
 */
export const deskDayOf = (zone?: string | null, now: Date = new Date()): string =>
  timeDeskDayOf(zone, now);

/**
 * The building's CALENDAR date ('YYYY-MM-DD') — what "today" means for every
 * date rule on a pass. A zone Intl rejects falls back to the reader's own day,
 * as the portal's clocks do.
 */
export const calendarDayOf = (zone?: string | null, now: Date = new Date()): string =>
  timeCalendarDayOf(zone, now);

/** How far ahead the preview will look for visits — the engine's own range cap. */
export const PREVIEW_HORIZON_DAYS = 400;

/**
 * What a schedule is made of. Wide on purpose: the editor has `days` (ISO
 * weekdays), the backend case file has `days_mask` (bit0 = Monday), and a saved
 * pass carries skips and a pause on top. Either day form is accepted.
 */
export interface ScheduleSpec {
  schedule_kind: PassScheduleKind;
  valid_from: string;
  valid_until?: string | null;
  days?: readonly number[] | null;
  days_mask?: number | null;
  interval_weeks?: number | null;
  window_start?: string | null;
  window_end?: string | null;
  skip_dates?: readonly string[] | null;
  status?: PassStatus | null;
  paused_until?: string | null;
}

/* ───────────────────────────  Calendar dates  ─────────────────────────── */

const DAY_MS = 86400000;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** Days since 1970-01-01 for a real 'YYYY-MM-DD', else null. */
const dayNumber = (value: string | null | undefined): number | null => {
  if (typeof value !== 'string') return null;
  const match = DATE_RE.exec(value);
  if (!match) return null;
  const ms = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(ms) || new Date(ms).toISOString().slice(0, 10) !== value) {
    return null;
  }

  return ms / DAY_MS;
};

const dateOf = (day: number): string =>
  new Date(day * DAY_MS).toISOString().slice(0, 10);

/** ISO weekday, 1 = Monday … 7 = Sunday. */
const isoWeekday = (day: number): number =>
  ((new Date(day * DAY_MS).getUTCDay() + 6) % 7) + 1;

/** True for a real calendar date written 'YYYY-MM-DD'. */
export const isRealDate = (value: string | null | undefined): boolean =>
  dayNumber(value) !== null;

/** True for a 24-hour 'HH:MM'. */
export const isClockTime = (value: string | null | undefined): boolean =>
  typeof value === 'string' && TIME_RE.test(value);

/** 'YYYY-MM-DD' + n days. An unreal date comes back unchanged. */
export const addDays = (date: string, n: number): string => {
  const day = dayNumber(date);

  return day === null ? date : dateOf(day + n);
};

/**
 * 'YYYY-MM-DD' + n calendar months, the day clamped to the target month's
 * length (31 Jan + 1 month = 28 or 29 Feb, never 3 Mar).
 */
export const addMonths = (date: string, n: number): string => {
  const match = DATE_RE.exec(date);
  if (!match || dayNumber(date) === null) return date;
  const total = Number(match[1]) * 12 + (Number(match[2]) - 1) + n;
  const year = Math.floor(total / 12);
  const month = total - year * 12;
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const day = Math.min(Number(match[3]), lastDay);

  return new Date(Date.UTC(year, month, day)).toISOString().slice(0, 10);
};

/** Whole days from a to b (b - a). NaN when either is not a real date. */
export const daysBetween = (a: string, b: string): number => {
  const from = dayNumber(a);
  const to = dayNumber(b);

  return from === null || to === null ? NaN : to - from;
};

/**
 * The first date on or after `from` that falls on one of `days` (ISO weekdays).
 * "Every 2 weeks" counts from the week of the start date, so a start that is
 * not a visiting day can put the first visit a fortnight away; a start moved
 * here is the first visit itself. Unchanged when there is nothing to move to.
 */
export const firstDayOnOrAfter = (from: string, days: readonly number[] | null | undefined): string => {
  const start = dayNumber(from);
  if (start === null || !days || !days.length) return from;
  for (let ahead = 0; ahead < 7; ahead += 1) {
    if (days.includes(isoWeekday(start + ahead))) return dateOf(start + ahead);
  }

  return from;
};

/** ISO weekdays out of a days_mask (bit0 = Monday). */
export const daysFromMask = (mask: number | null | undefined): number[] => {
  const out: number[] = [];
  const value = Number(mask) || 0;
  for (let d = 1; d <= 7; d += 1) {
    if (value & (1 << (d - 1))) out.push(d);
  }

  return out;
};

/** A days_mask out of ISO weekdays. Out-of-range entries are ignored. */
export const maskFromDays = (days: readonly number[] | null | undefined): number =>
  (days || []).reduce(
    (mask, d) => (Number.isInteger(d) && d >= 1 && d <= 7 ? mask | (1 << (d - 1)) : mask),
    0
  );

/* ─────────────────────────────  Time zones  ───────────────────────────── */

/** The zone Intl will actually use, or 'UTC' — the server's lb_safe_timezone rule. */
export const safeZone = (zone: string | null | undefined): string => timeSafeZone(zone);

/** How far the zone's wall clock is ahead of UTC at an instant, in ms. */
const offsetAt = timeOffsetAt;

/**
 * The instant a local wall-clock time names in a zone.
 *
 * Every offset the zone uses within a day either side is a candidate; a
 * candidate is REAL when the zone agrees it is in force at the instant it
 * gives. Two real ones = the repeated hour of a fall-back: the later instant
 * (standard time). None = the skipped hour of a spring-forward: the smaller
 * offset, which moves the wall time forward by the gap. Both are Postgres's
 * answers, which is what the fixture pins.
 */
export const zonedInstant = (date: string, time: string, zone: string): number => {
  const day = dayNumber(date);
  const clock = TIME_RE.exec(time);
  if (day === null || !clock) return NaN;
  const wall = day * DAY_MS + (Number(clock[1]) * 60 + Number(clock[2])) * 60000;
  if (zone === 'UTC') return wall;

  const offsets = Array.from(
    new Set([offsetAt(wall - DAY_MS, zone), offsetAt(wall, zone), offsetAt(wall + DAY_MS, zone)])
  );
  const real = offsets
    .map(offset => ({ offset, instant: wall - offset }))
    .filter(c => offsetAt(c.instant, zone) === c.offset)
    .map(c => c.instant);

  return real.length ? Math.max(...real) : wall - Math.min(...offsets);
};

/** ISO instant without milliseconds — the shape the case file writes. */
const isoInstant = (ms: number): string =>
  new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z');

const pad = (n: number): string => String(n).padStart(2, '0');
const DESK_DAY_START = `${pad(DESK_DAY_START_HOUR)}:00`;

/* ──────────────────────────────  Occurrences  ─────────────────────────── */

const hasWindow = (spec: ScheduleSpec): boolean =>
  isClockTime(spec.window_start) &&
  isClockTime(spec.window_end) &&
  spec.window_start !== spec.window_end;

const occurrenceOn = (day: number, spec: ScheduleSpec, zone: string): PassOccurrence => {
  const date = dateOf(day);
  if (!hasWindow(spec)) {
    return {
      local_date: date,
      starts_at: isoInstant(zonedInstant(date, DESK_DAY_START, zone)),
      ends_at: isoInstant(zonedInstant(dateOf(day + 1), DESK_DAY_START, zone)),
      all_day: true,
    };
  }
  const start = spec.window_start as string;
  const end = spec.window_end as string;
  const endDate = end <= start ? dateOf(day + 1) : date;

  return {
    local_date: date,
    starts_at: isoInstant(zonedInstant(date, start, zone)),
    ends_at: isoInstant(zonedInstant(endDate, end, zone)),
    all_day: false,
  };
};

/**
 * Every visit whose LOCAL date falls in [from, to], both inclusive — the
 * engine's own contract (lb_pass_occurrences(p, tz, from, to)).
 */
export const occurrencesBetween = (
  spec: ScheduleSpec | null | undefined,
  zone: string | null | undefined,
  from: string,
  to: string
): PassOccurrence[] => {
  if (!spec || spec.status === 'revoked') return [];
  const validFrom = dayNumber(spec.valid_from);
  const validUntil =
    spec.schedule_kind === 'once' ? validFrom : dayNumber(spec.valid_until);
  const rangeFrom = dayNumber(from);
  const rangeTo = dayNumber(to);
  if (validFrom === null || validUntil === null || rangeFrom === null || rangeTo === null) {
    return [];
  }

  const tz = safeZone(zone);
  const start = Math.max(rangeFrom, validFrom);
  const end = Math.min(rangeTo, validUntil, start + PREVIEW_HORIZON_DAYS);
  const mask =
    spec.days_mask !== null && spec.days_mask !== undefined
      ? Number(spec.days_mask)
      : maskFromDays(spec.days);
  const interval = Math.max(1, Math.floor(Number(spec.interval_weeks) || 1));
  // The Monday of valid_from's ISO week.
  const anchor = validFrom - (isoWeekday(validFrom) - 1);
  const skips = new Set(spec.skip_dates || []);
  const paused = spec.status === 'paused';
  const pausedUntil = spec.paused_until || null;

  const out: PassOccurrence[] = [];
  for (let day = start; day <= end; day += 1) {
    const date = dateOf(day);
    if (paused && (pausedUntil === null || date <= pausedUntil)) continue;
    if (skips.has(date)) continue;
    if (spec.schedule_kind !== 'once') {
      if (!(mask & (1 << (isoWeekday(day) - 1)))) continue;
      if (Math.floor((day - anchor) / 7) % interval !== 0) continue;
    }
    out.push(occurrenceOn(day, spec, tz));
  }

  return out;
};

/**
 * The next `n` visits from the local date `from` on — a PREVIEW (see the
 * header). With `now` (ms), a visit that has already finished is left out.
 */
export const previewOccurrences = (
  spec: ScheduleSpec | null | undefined,
  zone: string | null | undefined,
  from: string,
  n: number,
  now?: number
): PassOccurrence[] => {
  const first = dayNumber(from);
  if (!spec || first === null || n <= 0) return [];
  const last = first + PREVIEW_HORIZON_DAYS;
  const out: PassOccurrence[] = [];

  // A month at a time, so a daily pass costs one small expansion, not 400 days.
  for (let chunk = first; chunk <= last && out.length < n; chunk += 31) {
    const batch = occurrencesBetween(spec, zone, dateOf(chunk), dateOf(Math.min(chunk + 30, last)));
    for (const occurrence of batch) {
      if (now !== undefined && Date.parse(occurrence.ends_at) <= now) continue;
      out.push(occurrence);
      if (out.length >= n) break;
    }
  }

  return out;
};

/* ─────────────────────────────  The sentence  ─────────────────────────── */

// 1 Jan 2024 was a Monday, so day `iso` of that week is weekday `iso`.
const weekdayName = (iso: number, width: 'long' | 'short'): string => {
  try {
    return formatDate(Date.UTC(2024, 0, iso), { weekday: width, timeZone: 'UTC' });
  } catch {
    return String(iso);
  }
};

/** A calendar date as words, read as that date wherever the reader is. */
export const formatPassDate = (
  date: string,
  options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }
): string => {
  const day = dayNumber(date);
  if (day === null) return date;
  try {
    return formatDate(day * DAY_MS, { ...options, timeZone: 'UTC' });
  } catch {
    return date;
  }
};

/** "Tue 6 Oct" — the day of a visit, with the year only when it is not this one. */
export const formatVisitDay = (date: string, today?: string | null): string => {
  const sameYear = !today || today.slice(0, 4) === date.slice(0, 4);
  // The weekday is formatted ON ITS OWN and joined with a space: en-GB puts a
  // comma after the weekday as soon as a year is present ("Sat, 2 Jan 2027"),
  // so one combined format reads differently in December than in October.
  const weekday = formatPassDate(date, { weekday: 'short' });
  const rest = formatPassDate(date, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });

  return weekday === date ? rest : `${weekday} ${rest}`;
};

const joinAnd = (parts: string[]): string =>
  parts.length <= 1
    ? parts.join('')
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;

/** Consecutive runs of ISO weekdays: [1,2,3,5] -> [[1,2,3],[5]]. */
const runsOf = (days: number[]): number[][] =>
  days.reduce<number[][]>((runs, d) => {
    const last = runs[runs.length - 1];
    if (last && last[last.length - 1] === d - 1) last.push(d);
    else runs.push([d]);

    return runs;
  }, []);

const capitalise = (text: string): string =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

/** "Tuesday" / "Tuesday and Thursday" / "Mon–Fri" / "Mon, Wed and Fri" / "every day". */
const daysPhrase = (days: number[]): string => {
  if (days.length === 7) return 'every day';
  if (days.length === 1) return weekdayName(days[0], 'long');
  const runs = runsOf(days);
  if (runs.every(run => run.length === 1) && days.length === 2) {
    return joinAnd(days.map(d => weekdayName(d, 'long')));
  }
  const parts = runs.flatMap(run =>
    run.length >= 3
      ? [`${weekdayName(run[0], 'short')}–${weekdayName(run[run.length - 1], 'short')}`]
      : run.map(d => weekdayName(d, 'short'))
  );

  return joinAnd(parts);
};

const isSingleRange = (days: number[]): boolean => {
  const runs = runsOf(days);

  return runs.length === 1 && runs[0].length >= 3 && days.length < 7;
};

/** "Every Tuesday" / "Mon–Fri" / "Every other Friday" / "Mon–Fri, every 3 weeks". */
const repeatPhrase = (days: number[], interval: number): string => {
  const phrase = daysPhrase(days);
  if (interval <= 1) {
    if (days.length === 7) return 'Every day';

    return isSingleRange(days) ? phrase : `Every ${phrase}`;
  }
  if (days.length === 1) {
    return interval === 2
      ? `Every other ${phrase}`
      : `Every ${countOf(interval, 'week')} on ${phrase}`;
  }
  const every = interval === 2 ? 'every other week' : `every ${countOf(interval, 'week')}`;

  return `${capitalise(phrase)}, ${every}`;
};

/** "09:00–13:00" / "22:00–06:00 (next day)" / "any time". */
export const describeWindow = (
  start: string | null | undefined,
  end: string | null | undefined
): string => {
  if (!isClockTime(start) || !isClockTime(end) || start === end) return 'any time';

  return `${start}–${end}${(end as string) <= (start as string) ? ' (next day)' : ''}`;
};

export interface DescribeOptions {
  /** The building's calendar date. With it, a future start is named and a
   *  date in another year carries its year. */
  today?: string | null;
}

/**
 * The schedule as one sentence, in the building's wall-clock time:
 *
 *   Every Tuesday, 09:00–13:00, until 31 Dec 2026
 *   Every other Friday, any time
 *   Mon–Fri, 08:00–18:00
 *   Once, Sat 4 Oct, 10:00–12:00
 *   Every Friday, 22:00–06:00 (next day), until 31 Dec 2026
 */
export const describeSchedule = (
  spec: ScheduleSpec | null | undefined,
  options: DescribeOptions = {}
): string => {
  if (!spec) return '';
  const window = describeWindow(spec.window_start, spec.window_end);

  if (spec.schedule_kind === 'once') {
    if (!isRealDate(spec.valid_from)) return `Once, ${window}`;

    return `Once, ${formatVisitDay(spec.valid_from, options.today)}, ${window}`;
  }

  const days =
    spec.days_mask !== null && spec.days_mask !== undefined
      ? daysFromMask(spec.days_mask)
      : Array.from(new Set((spec.days || []).filter(d => d >= 1 && d <= 7))).sort(
          (a, b) => a - b
        );
  const interval = Math.max(1, Math.floor(Number(spec.interval_weeks) || 1));
  const parts = [days.length ? repeatPhrase(days, interval) : 'Regular visits', window];

  const startsLater =
    !!options.today && isRealDate(spec.valid_from) && spec.valid_from > options.today;
  if (startsLater) parts.push(`from ${formatPassDate(spec.valid_from)}`);
  if (isRealDate(spec.valid_until)) {
    const until = `until ${formatPassDate(spec.valid_until as string)}`;
    if (startsLater) parts[parts.length - 1] += ` ${until}`;
    else parts.push(until);
  }

  return parts.join(', ');
};

/* ──────────────────────────  One visit, in words  ─────────────────────── */

/** The reader's own zone, or '' when the platform will not say. */
export const deviceZone = (): string => timeDeviceZone();

/**
 * "Tue 6 Oct, 09:00" / "Tue 6 Oct, any time" — a visit in the BUILDING's
 * clock. The date is the occurrence's own local date (already the building's),
 * so only the time is converted, through clockTime with the building zone.
 */
export const describeVisit = (
  occurrence: Pick<PassOccurrence, 'local_date' | 'starts_at' | 'all_day'>,
  zone: string | null | undefined,
  today?: string | null
): string => {
  const day = formatVisitDay(occurrence.local_date, today);
  if (occurrence.all_day) return `${day}, any time`;
  const time = clockTime(occurrence.starts_at, safeZone(zone));

  return time ? `${day}, ${time}` : day;
};

/**
 * "Times are Sofia time." when the building's clock is not the reader's own,
 * '' when it is (or when there is no zone Intl will name — then the formatters
 * fell back, and naming a zone would be a claim they did not keep). The two
 * are compared by OFFSET: a browser still reports Asia/Calcutta for a building
 * the server calls Asia/Kolkata.
 */
export const zoneNote = (zone: string | null | undefined): string => {
  const label = zoneLabel(zone);
  if (!label) return '';
  if (sameClock(zone, deviceZone())) return '';

  return label === 'UTC' ? 'Times are UTC.' : `Times are ${label} time.`;
};
